import type { Plan } from '@/api/plan/types/plan';
import type { Post } from '@/api/post/types/post';

// The URL to load first, plus the original to fall back to when `src` is a
// pre-sized variant. `secondarySrc` is set only when it differs from `src`.
export type ImageSources = { src: string; secondarySrc?: string };

const clean = (url?: string | null) => (typeof url === 'string' ? url.trim() : '');

export const pickImageSources = (variant?: string | null, original?: string | null): ImageSources => {
  const preferred = clean(variant);
  const fallback = clean(original);
  if (preferred && fallback && preferred !== fallback) return { src: preferred, secondarySrc: fallback };
  return { src: preferred || fallback };
};

// Feed plan card (208x160). Deliberately never thumbnailUrl: that is 176 px wide,
// sized for the 88 px list rows, and visibly soft on a 3x feed card.
export const planFeedCardSources = (plan: Pick<Plan, 'feedCardImageUrl' | 'imageUrl'>) =>
  pickImageSources(plan.feedCardImageUrl, plan.imageUrl);

// Post card, Post View and the story viewer.
export const postDisplaySources = (post: Pick<Post, 'imageDisplayUrl' | 'image'>) =>
  pickImageSources(post.imageDisplayUrl, post.image);

// Profile grid cells.
export const postGridSources = (post: Pick<Post, 'imageGridUrl' | 'image'>) =>
  pickImageSources(post.imageGridUrl, post.image);

// Variant URLs that failed this session, so other cards showing the same image
// go straight to the original instead of requesting a known-broken URL again.
const MAX_FAILED_VARIANTS = 500;
export const failedVariantUrls = new Set<string>();

export const rememberFailedVariant = (url: string) => {
  if (failedVariantUrls.size >= MAX_FAILED_VARIANTS) failedVariantUrls.clear();
  failedVariantUrls.add(url);
};

const preloadImage = (src: string) =>
  new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });

// Preloads the variant, falling back to the original; rejects only if neither loads.
export const preloadImageSources = async ({ src, secondarySrc }: ImageSources) => {
  const first = secondarySrc && failedVariantUrls.has(src) ? secondarySrc : src;
  try {
    await preloadImage(first);
  } catch (error) {
    if (!secondarySrc || first === secondarySrc) throw error;
    rememberFailedVariant(src);
    await preloadImage(secondarySrc);
  }
};
